import useTranslation from "next-translate/useTranslation";
import { updateSetting, useSettings } from "../../redux/settings";
import styles from "./styles/Settings.module.css";
import pieceStyles from "@/chessBoard/components/Piece.module.css";
import { ChessSets } from "@chesshotel/constants";

const getPieceUrl = (chessSet: string, piece: string) =>
  `/images/pieces/${chessSet}/${piece}.svg`;

interface ChessSetPreviewProps {
  chessSet: string;
  lightColor: string;
  darkColor: string;
  rows?: number;
}

export function ChessSetPreview({
  chessSet,
  lightColor,
  darkColor,
  rows = 2,
}: ChessSetPreviewProps) {
  const pieces = [
    { piece: "bR", col: 0, row: 0 },
    { piece: "bN", col: 1, row: 0 },
    { piece: "bB", col: 2, row: 0 },
    { piece: "bQ", col: 3, row: 0 },
    { piece: "bP", col: 0, row: 1 },
    { piece: "bP", col: 1, row: 1 },
    { piece: "bP", col: 2, row: 1 },
    { piece: "bP", col: 3, row: 1 },
  ];

  const cols = 4;
  const squareSize = 100;
  const viewBoxHeight = rows * squareSize;

  // Generate checkerboard squares dynamically
  const squares = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      squares.push({
        x: col * squareSize,
        y: row * squareSize,
        isLight: (row + col) % 2 === 0,
      });
    }
  }

  // Piece height as percentage: 100% / rows
  const pieceHeightPercent = 100 / rows;

  return (
    <div className="flex overflow-hidden relative rounded border border-gray-200 md:w-64">
      {pieces.map((p, i) => (
        <div
          key={i}
          className={pieceStyles.settings_piece}
          style={{
            transform: `translate(${p.col * 100}%, ${p.row * 100}%)`,
            backgroundImage: `url(${getPieceUrl(chessSet, p.piece)})`,
            height: `${pieceHeightPercent}%`,
          }}
        />
      ))}

      <svg
        width="100%"
        viewBox={`0 0 ${cols * squareSize} ${viewBoxHeight}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <g id="Light" fill={lightColor}>
          {squares
            .filter((s) => s.isLight)
            .map((s, i) => (
              <rect
                key={i}
                x={s.x}
                y={s.y}
                width={squareSize}
                height={squareSize}
              />
            ))}
        </g>
        <g id="Dark" fill={darkColor}>
          {squares
            .filter((s) => !s.isLight)
            .map((s, i) => (
              <rect
                key={i}
                x={s.x}
                y={s.y}
                width={squareSize}
                height={squareSize}
              />
            ))}
        </g>
      </svg>
    </div>
  );
}

const CHESS_SET_CONFIG: Record<
  string,
  { lightColor: string; darkColor: string }
> = {
  [ChessSets.CHESSHOTEL]: { lightColor: "#f6f6f6", darkColor: "#ac9c8d" },
  [ChessSets.CHESSHOTEL_NEW]: { lightColor: "#EDD6B0", darkColor: "#B98761" },
  [ChessSets.STAUNTY]: { lightColor: "#EDD6B0", darkColor: "#B98761" },
};

interface ChessSetProps {
  showTitle?: boolean;
}

export default function ChessSet({ showTitle = true }: ChessSetProps) {
  const { t: settingsT } = useTranslation("auth");
  const { chessSet } = useSettings();

  const config =
    CHESS_SET_CONFIG[chessSet] || CHESS_SET_CONFIG[ChessSets.CHESSHOTEL];

  return (
    <div className={showTitle ? styles.body : ""}>
      {showTitle && (
        <div className={styles.title}>{settingsT("Chess sets")}</div>
      )}
      <div className="flex flex-col gap-4 md:flex-row md:gap-8">
        <div>
          <select
            value={chessSet}
            onChange={(e) => {
              updateSetting("chessSet", e.target.value);
            }}
            className="md:w-64"
          >
            <option value={ChessSets.CHESSHOTEL}>ChessHotel Classic</option>
            <option value={ChessSets.CHESSHOTEL_NEW}>ChessHotel Modern</option>
            <option value={ChessSets.STAUNTY}>Staunty</option>
          </select>
        </div>

        <ChessSetPreview
          chessSet={chessSet}
          lightColor={config.lightColor}
          darkColor={config.darkColor}
        />
      </div>
    </div>
  );
}
